// Custom file picker HTML helper. Replaces the native "Choose file / No file
// chosen" control whose appearance is unstyleable and whose displayed
// filename is wiped on every re-render (because setState replaces the
// <input type="file"> node and the browser security model forbids
// programmatically setting input.files).
//
// The wrapping <label> acts as the click target — clicking either the
// "Choose file" pill or the filename area opens the picker via native label
// semantics. The filename is rendered from the supplied `file` (or `name`)
// arg so the component owns the source of truth, not the DOM input.

import { esc } from './component.js';
import { icon } from './icons.js';
import { t } from './i18n.js';

/**
 * @param {{
 *   inputAttrs?: string,   // raw HTML attrs for the hidden <input> (accept, name, data-role, required, multiple, etc.)
 *   file?: File|null,      // currently-selected File; preferred over `name`
 *   files?: File[]|null,   // when present (multi-select), supersedes `file` and shows a chip list
 *   name?: string,         // fallback display name when no File handle is available
 *   disabled?: boolean,    // disables the trigger and visually dims the control
 *   clearable?: boolean,   // render an inline "clear" button (default true when a file is selected)
 *   id?: string,           // optional id for the input (lets external <label for=""> drive it)
 *   variant?: 'pill'|'drop-zone', // 'pill' (default) = inline button + name; 'drop-zone' = tall centered target.
 *   title?: string,        // drop-zone only: main label (default = t('common.drop_or_browse'))
 *   subLabel?: string,     // drop-zone only: secondary hint line (e.g. accepted types / size)
 * }} opts
 */
export function filePickerHtml({
  inputAttrs = '',
  file = null,
  files = null,
  name = '',
  disabled = false,
  clearable = true,
  id = '',
  variant = 'pill',
  title = '',
  subLabel = '',
} = {}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  const displayName = list.length > 0
    ? (list.length === 1 ? list[0].name : `${list.length} ${t('common.files_selected')}`)
    : ((file && file.name) || name || '');
  const hasFile = displayName !== '';
  const idAttr = id ? ` id="${esc(id)}"` : '';
  const isDropZone = variant === 'drop-zone';
  const cls = ['file-picker'];
  if (hasFile) cls.push('is-filled');
  if (disabled) cls.push('is-disabled');
  if (isDropZone) cls.push('is-drop-zone');
  const clearBtn = hasFile && clearable ? `
        <button type="button" class="file-picker-clear" data-action="clear-file"
                aria-label="${esc(t('common.clear_file'))}">
          ${icon('x', { size: 'sm' })}
        </button>` : '';
  // When multiple files are picked, render a chip list below the name so the
  // user can see exactly what got attached (and which page is which).
  const chipsHtml = list.length > 1 ? `
        <ul class="file-picker-chips" aria-label="${esc(t('common.files_selected'))}">
          ${list.map((f, i) => `<li><span class="file-picker-chip-index">${i + 1}</span><span class="file-picker-chip-name">${esc(f.name)}</span></li>`).join('')}
        </ul>` : '';
  if (isDropZone) {
    const dzTitle = title || t('common.drop_or_browse');
    // The .file-picker-title is hidden via CSS when the parent has
    // .is-filled, so _syncPickerLabel only has to update .file-picker-name
    // in place; the title slot is always present in the DOM.
    return `
    <label class="${cls.join(' ')}">
      <input type="file"${idAttr} ${inputAttrs} ${disabled ? 'disabled' : ''} />
      <span class="file-picker-icon" aria-hidden="true">${icon('upload', { size: 'lg' })}</span>
      <span class="file-picker-title">${esc(dzTitle)}</span>
      <span class="file-picker-name${hasFile ? '' : ' is-empty'}">${hasFile ? esc(displayName) : ''}</span>
      ${chipsHtml}
      ${subLabel ? `<span class="file-picker-sub">${esc(subLabel)}</span>` : ''}
      ${clearBtn}
    </label>
  `;
  }
  return `
    <label class="${cls.join(' ')}">
      <input type="file"${idAttr} ${inputAttrs} ${disabled ? 'disabled' : ''} />
      <span class="file-picker-trigger" aria-hidden="true">
        ${icon('upload', { size: 'sm' })}<span>${esc(t('common.choose_file'))}</span>
      </span>
      <span class="file-picker-name${hasFile ? '' : ' is-empty'}">
        ${hasFile ? esc(displayName) : esc(t('common.no_file_selected'))}
      </span>
      ${chipsHtml}
      ${clearBtn}
    </label>
  `;
}

// Idempotently wires drag-and-drop on every `.file-picker.is-drop-zone`
// under `root`. The drop assigns DataTransfer.files to the hidden <input>
// and fires a synthetic 'change' so existing change-handlers run unchanged.
const WIRED = new WeakSet();
export function wireDropZones(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  root.querySelectorAll('.file-picker.is-drop-zone').forEach((label) => {
    if (WIRED.has(label)) return;
    WIRED.add(label);
    const input = label.querySelector('input[type="file"]');
    if (!input) return;
    label.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (input.disabled) return;
      label.classList.add('is-dragover');
    });
    label.addEventListener('dragleave', () => label.classList.remove('is-dragover'));
    label.addEventListener('drop', (e) => {
      e.preventDefault();
      label.classList.remove('is-dragover');
      if (input.disabled) return;
      const dt = e.dataTransfer;
      if (!dt || !dt.files || !dt.files[0]) return;
      try { input.files = dt.files; } catch { return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}
